import {
  type AccountOpeningCollectionCopy,
  type AccountOpeningCollectionStep,
  type AccountOpeningDescriptor,
  type AccountOpeningFlowCopy,
  type AccountOpeningHandoffCopy,
  type AccountOpeningHandoffStep,
  type AccountOpeningHoldersCopy,
  type AccountOpeningHoldersPanel,
  type AccountOpeningItemCopy,
  type AccountOpeningOpeningsCopy,
  type AccountOpeningOperationCopy,
  type AccountOpeningOperationStep,
  type AccountOpeningTracking,
} from '../../../models/AccountOpeningDescriptor'
import { presentString } from '../domain/schemaValues'

const fallback = (served: string | undefined, standIn: string): string => (served?.trim() ? served : standIn)

const withDefaults = <T extends Record<string, string>>(defaults: T, served: Partial<Record<keyof T, string | undefined>> | undefined): T =>
  Object.fromEntries(Object.entries(defaults).map(([key, standIn]) => [key, fallback(served?.[key as keyof T], standIn)])) as T

const optional = (served: string | undefined): string | undefined => served?.trim() || undefined

const COLLECTION_COPY_DEFAULTS: Required<AccountOpeningCollectionCopy> = {
  gatedHint: "You'll be able to fill this in once the steps above are confirmed.",
  loading: 'Fetching this step’s data',
  empty: 'There is nothing to show here yet.',
  railHeading: 'To complete this step:',
  railMissing: 'Still missing required information',
  railInvalid: 'The custodian will not accept these as entered',
  railConfirm: 'Confirm that all the data is valid',
}

export const ITEM_COPY_DEFAULTS: Required<AccountOpeningItemCopy> = {
  accountHeading: 'New account',
  submittedAccountHeading: 'Account',
  accountSettingsHeading: 'Account settings',
  partyNameLabel: 'Name',
  partyRoleNote: 'These are the details the custodian asks for as {role}.',
  partyFallbackName: 'Party {index}',
}

const OPENINGS_COPY_DEFAULTS: Required<AccountOpeningOpeningsCopy> = {
  empty: 'No accounts have been submitted yet. Start an application to open one at the custodian.',
  draftStatus: 'In progress',
  submittedStatus: 'Submitted',
  deleteTitle: 'Delete this submission?',
  deleteBody:
    'This removes Zocks’ record of the submission. It does not withdraw the application at the custodian — do that in their portal if you need it cancelled.',
  deleteReference: 'Custodian reference:',
}

const OPERATION_COPY_DEFAULTS: Required<Omit<AccountOpeningOperationCopy, 'actionAgain' | 'warning' | 'lastRun'>> = {
  body: 'This step sends the application to the custodian and reports what they said.',
  action: 'Continue',
  pending: 'Working…',
  success: 'The custodian accepted the application.',
  hint: 'This can take a moment.',
  issuesHeading: 'The custodian reported these issues:',
  linkLabel: 'Finish this submission at the custodian',
  linkMissing: 'The custodian did not return a hand-off link. Open it from their portal using the reference above.',
  referenceLabel: 'Reference',
  tooManyItems: 'Only the first account will be sent — the custodian takes {limit} account per submission. Open the other {excess} separately.',
  notReady: 'Confirm the steps above first so the draft can be saved.',
}

const TRACKING_COPY_DEFAULTS = {
  refreshLabel: 'Check for updates',
  checkingLabel: 'Checking with the custodian…',
  checkedAtLabel: 'Checked {time}',
  checkFailedLabel: 'Could not reach the custodian — showing the last known status',
}

const HANDOFF_COPY_DEFAULTS: Required<AccountOpeningHandoffCopy> = {
  linkLabel: 'Open this submission at the custodian',
  missing: 'No hand-off link was stored for this submission. Open it from the custodian’s portal using the reference above.',
}

const FLOW_COPY_DEFAULTS: Required<Omit<AccountOpeningFlowCopy, 'items' | 'openings'>> = {
  editHeading: 'New client onboarding',
  readOnlyHeading: 'Review account submission',
  intakeTitle: 'Open Account',
  leaveLabel: 'Back to Account Opening',
  doneMessage: 'Your account opening is complete.',
  doneLabel: 'Done',
  custodianLabel: 'Custodian',
  noAccountTypes: 'This custodian returned no account types. Check the integration connection before starting an application.',
}

const CONFIRM_COPY_DEFAULTS = {
  label: 'Confirm',
  readyMessage: 'Confirm once this is correct.',
  blockedMessage: 'This can be confirmed once every required field is filled in.',
  itemBlockedMessage: 'Still missing: {fields}',
}

export const PREFILL_COPY_DEFAULTS = {
  label: 'Fill from meeting or contact',
}

const HOLDERS_COPY_DEFAULTS = {
  heading: 'Holders',
  roleLabel: 'Role',
  relationshipLabel: 'Relationship',
  relationshipUnset: 'Not specified',
  entityNamePlaceholder: 'Add the legal name',
  personNamePlaceholder: 'Add holder name',
  addLabel: 'Add holder',
  removeLabel: 'Remove holder',
}

const awaitingTypeDefault = (axisCount: number): string =>
  axisCount > 1 ? 'You can fill these in once {axes} are selected.' : 'You can fill these in once {axes} is selected.'

export const holdersCopy = (panel: AccountOpeningHoldersPanel, axisCount: number, axisNames: string): Required<AccountOpeningHoldersCopy> => ({
  ...withDefaults(HOLDERS_COPY_DEFAULTS, panel.copy),
  awaitingType: fallback(panel.copy?.awaitingType, awaitingTypeDefault(axisCount)).replace('{axes}', axisNames),
})

export const NO_VALUE = '—'

export const WIZARD_ERRORS = {
  resume: 'Could not open that application. Try again in a moment.',
  delete: 'That application could not be deleted. Try again in a moment.',
  save: 'Saving the draft failed. Your changes are still on screen — try again.',
  operationRefused: 'The custodian rejected this without giving a reason. Check the steps above and try again.',
  operationFailed: 'The request did not complete. Nothing was sent — try again in a moment.',
  prefill: 'Could not fill this from the meeting or contact. Fill the fields in manually, or try again.',
  prepare: 'Could not load the fields for this step. Edit any field above to try again.',
}

export const collectionCopy = (step: AccountOpeningCollectionStep): Required<AccountOpeningCollectionCopy> => withDefaults(COLLECTION_COPY_DEFAULTS, step.copy)

export const operationCopy = (
  step: AccountOpeningOperationStep,
): Required<Omit<AccountOpeningOperationCopy, 'actionAgain' | 'warning' | 'lastRun'>> &
  Pick<AccountOpeningOperationCopy, 'actionAgain' | 'warning' | 'lastRun'> => ({
  ...withDefaults(OPERATION_COPY_DEFAULTS, step.copy),
  actionAgain: optional(step.copy?.actionAgain),
  warning: optional(step.copy?.warning),
  lastRun: optional(step.copy?.lastRun),
})

export const tooManyItemsText = (template: string, limit: number, excess: number): string =>
  template.replace('{limit}', String(limit)).replace('{excess}', String(excess))

export const missingFieldsText = (template: string, labels: string[]): string => template.replace('{fields}', labels.join(', '))

export const accountHeading = (index: number, submitted = false, copy: Required<AccountOpeningItemCopy> = ITEM_COPY_DEFAULTS): string => {
  const noun = submitted ? copy.submittedAccountHeading : copy.accountHeading
  return index === 0 ? noun : `${noun} #${index + 1}`
}

export const accountSettingsHeading = (schema: Record<string, unknown>, copy: Required<AccountOpeningItemCopy> = ITEM_COPY_DEFAULTS): string =>
  presentString(schema.title) ?? copy.accountSettingsHeading

export const handoffCopy = (step: AccountOpeningHandoffStep): Required<AccountOpeningHandoffCopy> => withDefaults(HANDOFF_COPY_DEFAULTS, step.copy)

export const trackingCopy = (tracking: AccountOpeningTracking | undefined): typeof TRACKING_COPY_DEFAULTS => withDefaults(TRACKING_COPY_DEFAULTS, tracking)

export const flowCopy = (descriptor: AccountOpeningDescriptor | undefined): typeof FLOW_COPY_DEFAULTS => withDefaults(FLOW_COPY_DEFAULTS, descriptor?.copy)

export const itemCopy = (descriptor: AccountOpeningDescriptor | undefined): Required<AccountOpeningItemCopy> =>
  withDefaults(ITEM_COPY_DEFAULTS, descriptor?.copy?.items)

export const openingsCopy = (descriptor: AccountOpeningDescriptor | undefined): Required<AccountOpeningOpeningsCopy> =>
  withDefaults(OPENINGS_COPY_DEFAULTS, descriptor?.copy?.openings)

export const confirmCopy = (step: AccountOpeningCollectionStep) => ({
  ...withDefaults(CONFIRM_COPY_DEFAULTS, step.confirm),
  discardLabel: optional(step.confirm?.discardLabel),
})
